import { Booking } from "./booking.model";
import { Payment } from "./payment.mode";
import { Trip } from "./trip.model";
import { User } from "./user.model";

export enum NotificationStatus {
  PENDING = "Pending",
  SENT = "Sent",
  FAILED = "Failed",
}

export enum NotificationType {
  LOYALTY = "Loyalty",
  BOOKING = "Booking",
  TRIP = "Trip",
  PAYMENT = "Payment",
  SYSTEM = "System",
  APPOINTMENT = "Appointment",
}

export interface Notifications {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: string;
  status: string;
  isRead: boolean;
  createdAt: Date;
  updatedAt: Date;
  bookingId?: string;
  tripId?: string;
  paymentId?: string;
  loyaltyTransactionId?: string;
  actionUrl?: string;

  user: User;
  booking: Booking;
  trip: Trip;
  payment: Payment;
}

export class EmailNotification {
  recipient: string = "";
  subject: string = "";
  body: string = "";
  isHtml: boolean = true;
}

export class SmsNotification {
  phoneNumber: string = "";
  message: string = "";
}

export class BookingNotification {
  sendEmail: boolean;
  email: string = "";
  sendSms: boolean;
  phoneNumber: string = "";
}

// src/app/services/models/push-notification.model.ts
export interface PushNotificationDTO {
  title: string;
  message: string;
  imageUrl?: string;
  actionUrl?: string;
  data?: { [key: string]: string };
  priority: number;
  fcmTokens?: string[];
  userIds?: string[];
}

export interface FcmTokenUpdateModel {
  fcmToken: string;
  platform: string;
  appVersion?: string;
}

export interface PushNotificationLogDTO {
  id: string;
  notificationId: string;
  userId: string;
  title: string;
  message: string;
  platform: string;
  provider: string;
  status: string;
  fcmToken?: string;
  errorMessage?: string;
  sentAt?: Date;
  deliveredAt?: Date;
  retryCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface PushNotificationStatsDTO {
  totalSent: number;
  totalDelivered: number;
  totalFailed: number;
  deliveryRate: number;
  brevoPushUsed: number;
  brevoPushRemaining: number;
  byProvider: { [key: string]: number };
  byPlatform: { [key: string]: number };
}

export interface SendPushNotificationModel {
  title: string;
  message: string;
  targetType: "user" | "users" | "all";
  userId?: string;
  userIds?: string[];
  actionUrl?: string;
  data?: { [key: string]: string };
}

export interface NotificationWithPushDTO {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: string;
  status: string;
  isRead: boolean;
  bookingId?: string;
  tripId?: string;
  paymentId?: string;
  sendPushNotification: boolean;
  pushPayload?: string;
  imageUrl?: string;
  actionUrl?: string;
  priority: number;
  pushLogs?: PushNotificationLogDTO[];
  createdAt: Date;
  updatedAt: Date;
}

export enum PushNotificationStatus {
  PENDING = "Pending",
  SENT = "Sent",
  DELIVERED = "Delivered",
  FAILED = "Failed",
}

export enum PushNotificationProvider {
  FIREBASE = "Firebase",
  BREVO = "Brevo",
}

export enum DevicePlatform {
  ANDROID = "Android",
  IOS = "iOS",
  WEB = "Web",
}

// Extension du modèle User existant
export interface UserWithPushInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fcmToken?: string;
  pushNotificationsEnabled: boolean;
  lastTokenUpdate?: Date;
  devicePlatform?: string;
  appVersion?: string;
  isActive: boolean;
}

// Modèles pour les composants
export interface PushNotificationFilter {
  dateFrom?: Date;
  dateTo?: Date;
  status?: string;
  provider?: string;
  platform?: string;
  userId?: string;
}

export interface PushCampaign {
  id?: string;
  name: string;
  title: string;
  message: string;
  imageUrl?: string;
  actionUrl?: string;
  data?: { [key: string]: string };
  targetUserIds: string[];
  scheduledFor?: Date | string;
  status: "Draft" | "Scheduled" | "Sending" | "Sent" | "Completed" | "Failed" | "Cancelled";
  totalTargets?: number;
  totalSent?: number;
  totalDelivered?: number;
  totalFailed?: number;
  totalOpened?: number;
  completedAt?: Date | string;
  createdBy?: string;
  creatorName?: string;
  approvedBy?: string;
  approverName?: string;
  approvedAt?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface PushCampaignTarget {
  id?: string;
  pushCampaignId: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  fcmToken?: string;
  status: "Pending" | "Sent" | "Delivered" | "Failed" | "Opened";
  errorMessage?: string;
  sentAt?: Date | string;
  deliveredAt?: Date | string;
  openedAt?: Date | string;
  retryCount?: number;
  createdAt?: Date | string;
}

export interface CampaignStats {
  campaignId: string;
  campaignName: string;
  totalTargets: number;
  totalSent: number;
  totalDelivered: number;
  totalFailed: number;
  totalOpened: number;
  sendRate: number;
  deliveryRate: number;
  openRate: number;
  failureRate: number;
  status: string;
  scheduledFor?: Date | string;
  completedAt?: Date | string;
  createdAt: Date | string;
}

export interface CreateCampaignModel {
  name: string;
  title: string;
  message: string;
  targetUserIds: string[];
  scheduledFor?: Date | string;
  imageUrl?: string;
  actionUrl?: string;
  data?: { [key: string]: string };
}

export interface UserSegment {
  id: string;
  name: string;
  criteria: SegmentCriteria;
  userCount: number;
}

export interface SegmentCriteria {
  platform?: DevicePlatform[];
  lastActiveAfter?: Date;
  loyaltyLevel?: string[];
  hasBookings?: boolean;
  registeredAfter?: Date;
  registeredBefore?: Date;
}
